import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import { Vehicle, VehicleStatus } from '../../types';
import { vehicleService } from '../../services/api';
import { getStatusLabel } from '../../utils/vehicleUtils';

interface StatusUpdateModalProps {
  vehicle: Vehicle;
  initialStatus: VehicleStatus;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const STATUS_OPTIONS: Array<{ value: VehicleStatus; label: string; icon: string }> = [
  { value: 'out', label: 'Sorti', icon: '📤' },
  { value: 'returned', label: 'Rentré', icon: '📥' },
  { value: 'under_maintenance', label: 'En maintenance', icon: '🔧' },
  { value: 'washing', label: 'Lavage', icon: '💧' },
  { value: 'ready', label: 'Prêt', icon: '✓' },
];

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  vehicle,
  initialStatus,
  open,
  onClose,
  onSuccess
}) => {
  const [status, setStatus] = useState<VehicleStatus>(initialStatus);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      await vehicleService.update(vehicle.id, { status });
      
      // TODO: Add note if provided
      // if (note.trim()) {
      //   await vehicleNotesService.createNote(vehicle.id, note.trim());
      // }

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6">Changer Statut</Typography>
        <Typography variant="body2" color="text.secondary">
          {vehicle.registration_number} • {vehicle.marque} {vehicle.modele}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Statut actuel: <strong>{getStatusLabel(vehicle.status)}</strong>
          </Typography>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Nouveau statut:
        </Typography>

        <RadioGroup value={status} onChange={(e) => setStatus(e.target.value as VehicleStatus)}>
          {STATUS_OPTIONS.map(option => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{option.icon}</span>
                  <span>{option.label}</span>
                </Box>
              }
              sx={{
                border: '1px solid',
                borderColor: status === option.value ? 'primary.main' : 'divider',
                borderRadius: 1,
                mb: 1,
                mx: 0,
                px: 2,
                py: 0.5,
              }}
            />
          ))}
        </RadioGroup>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Note (optionnel)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 2 }}
          placeholder="Ajouter un commentaire..."
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || status === vehicle.status}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusUpdateModal;

